/**
 * Iter 249 — Commercial Panel : grille drag&drop de widgets configurable par chaque commercial.
 *
 * Le commercial peut :
 *  - Réorganiser les widgets (drag&drop)
 *  - Redimensionner chaque widget
 *  - Ajouter / retirer des widgets depuis le catalogue
 *  - Restaurer la disposition par défaut
 *
 * Layout persisté par user_id dans la collection `commercial_panel_layouts`.
 */
import { useEffect, useMemo, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Plus, RotateCcw, Lock, Unlock, X, Loader2, Sparkles, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { WIDGET_COMPONENTS, getWidgetIcon } from "../components/commercial-panel/widgets";

const ReactGridLayout = WidthProvider(GridLayout);

export default function CommercialPanelPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [layoutR, catalogR] = await Promise.all([
          api.get("/commercial-panel/layout"),
          api.get("/commercial-panel/catalog"),
        ]);
        if (cancelled) return;
        setLayout(layoutR.data.layout || []);
        setCatalog(catalogR.data.items || []);
      } catch (e) {
        toast.error("Impossible de charger votre panel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const handleLayoutChange = (newLayout) => {
    if (!editMode) return;
    // Preserve item ids; react-grid-layout doesn't always carry them in `i`
    const sanitized = newLayout.map((it) => ({
      i: it.i, x: it.x, y: it.y, w: it.w, h: it.h,
    }));
    setLayout(sanitized);
    setDirty(true);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      await api.put("/commercial-panel/layout", { layout });
      setDirty(false);
      toast.success("Disposition enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec sauvegarde");
    } finally { setSaving(false); }
  };

  const resetLayout = async () => {
    if (!window.confirm("Restaurer la disposition par défaut ?")) return;
    try {
      const r = await api.post("/commercial-panel/layout/reset");
      setLayout(r.data.layout || []);
      setDirty(false);
      toast.success("Disposition par défaut restaurée");
    } catch (e) {
      toast.error("Échec restauration");
    }
  };

  const addWidget = (widgetId) => {
    if (layout.find((l) => l.i === widgetId)) {
      toast.warning("Ce widget est déjà sur votre panel");
      return;
    }
    const cat = catalog.find((c) => c.id === widgetId);
    const w = cat?.default?.w || 4;
    const h = cat?.default?.h || 5;
    // Find the lowest Y position
    const maxY = layout.reduce((acc, it) => Math.max(acc, it.y + it.h), 0);
    setLayout((p) => [...p, { i: widgetId, x: 0, y: maxY, w, h }]);
    setDirty(true);
    setCatalogOpen(false);
    toast.success(`Widget "${cat?.label}" ajouté`);
  };

  const removeWidget = (widgetId) => {
    setLayout((p) => p.filter((l) => l.i !== widgetId));
    setDirty(true);
  };

  const toggleEditMode = () => {
    if (editMode && dirty) {
      if (!window.confirm("Quitter le mode édition sans sauvegarder ?")) return;
      // Revert by reloading
      api.get("/commercial-panel/layout").then((r) => {
        setLayout(r.data.layout || []);
        setDirty(false);
      });
    }
    setEditMode(!editMode);
  };

  const availableWidgets = catalog.filter((c) => !layout.find((l) => l.i === c.id));

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-4" data-testid="commercial-panel-page">
      <PageHeader
        icon={<LayoutDashboard />}
        title="Mon panel commercial"
        description="Centralisez vos outils de prospection : traducteur, fiche client, pipeline, RDV — tout sur une seule page configurable."
      />

      {/* Toolbar */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="panel-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={toggleEditMode}
            variant={editMode ? "default" : "outline"}
            size="sm"
            className={editMode ? "bg-cyan-500 hover:bg-cyan-400 text-[#14101c] font-bold" : ""}
            data-testid="toggle-edit-mode"
          >
            {editMode ? <Unlock className="w-3.5 h-3.5 mr-1.5" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
            {editMode ? "Mode édition (déverrouillé)" : "Réorganiser"}
          </Button>
          {editMode && (
            <>
              <Button onClick={() => setCatalogOpen(true)} variant="outline" size="sm" data-testid="open-catalog">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter un widget
              </Button>
              <Button onClick={resetLayout} variant="ghost" size="sm" className="text-slate-400" data-testid="reset-layout">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Réinitialiser
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode && dirty && (
            <span className="text-[11px] text-amber-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Modifications non sauvegardées
            </span>
          )}
          {editMode && (
            <Button
              onClick={saveLayout}
              disabled={saving || !dirty}
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-400 text-[#14101c] font-bold"
              data-testid="save-layout"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> :
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      ) : layout.length === 0 ? (
        <div className="text-center py-16 bg-[#14101c] border border-white/5 rounded-xl">
          <div className="text-slate-400 mb-3">Votre panel est vide</div>
          <Button onClick={() => { setEditMode(true); setCatalogOpen(true); }} className="bg-cyan-500 hover:bg-cyan-400 text-[#14101c]">
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter votre premier widget
          </Button>
        </div>
      ) : (
        <ReactGridLayout
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={50}
          isDraggable={editMode}
          isResizable={editMode}
          draggableHandle=".drag-handle"
          onLayoutChange={handleLayoutChange}
          margin={[12, 12]}
          containerPadding={[0, 0]}
        >
          {layout.map((item) => {
            const Component = WIDGET_COMPONENTS[item.i];
            if (!Component) return <div key={item.i} />;
            return (
              <div key={item.i} className="relative" data-testid={`grid-item-${item.i}`}>
                {editMode && (
                  <button
                    onClick={() => removeWidget(item.i)}
                    className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-400 text-white flex items-center justify-center shadow-lg"
                    title="Retirer ce widget"
                    data-testid={`remove-widget-${item.i}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <Component api={api} />
              </div>
            );
          })}
        </ReactGridLayout>
      )}

      {/* Widget catalog modal */}
      {catalogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setCatalogOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#14101c] border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="widget-catalog-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/5 sticky top-0 bg-[#14101c]">
              <h3 className="text-base font-bold text-white">Catalogue de widgets</h3>
              <button onClick={() => setCatalogOpen(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">
              {availableWidgets.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">
                  Vous avez déjà ajouté tous les widgets disponibles ✨
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableWidgets.map((w) => {
                    const Icon = getWidgetIcon(w.icon);
                    return (
                      <button
                        key={w.id}
                        onClick={() => addWidget(w.id)}
                        className="text-left p-3 rounded border border-white/10 bg-white/[0.02] hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-colors"
                        data-testid={`catalog-${w.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm text-white font-bold">{w.label}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mb-2">{w.description}</div>
                        <span className="text-[9px] uppercase text-slate-600">{w.category}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
